import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";

/**
 * Hook that returns a function to check if the user can manage stack role bindings.
 * User needs to be admin, or have SpaceAdmin or StackManage action in stack's space
 * Stacks created from a template deployment (blueprintDeployment) cannot have role bindings managed.
 *
 * @returns {(stack: Stack) => AccessResult} A function that takes a stack and returns an AccessResult
 */
const useCanManageStackRoleBindings = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      // Admin has full access
      if (viewer.admin) {
        return { result: true };
      }

      // Check if user has SpaceAdmin OR StackManage in stack's space
      if (
        hasAtLeastOneRequiredAction(
          [Action.SpaceAdmin, Action.StackManage],
          viewer,
          stack.spaceDetails?.id || stack.space
        )
      ) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer]
  );
};

export default useCanManageStackRoleBindings;
